import {
  UNIT_H, UNIT_W, UNIT_HP, FP, PFP, PROJ_LAVA_BOMB, WEAPON_PISTOL, WEAPON_LASER, WEAPON_GRENADE, WEAPON_SANDBAG, WEAPON_MINE, GRENADE_FUSE_TICKS, TICKS_PER_SECOND,
  CHEST_W, CHEST_FALLING, CHEST_SHORE, MINE_HALF, MINE_ICE, LEGACY_TURNS, isArmed,
  WEAPON_CRYO, WEAPON_ICE_MINE, WEAPON_METEOR, WEAPON_COCKTAIL, WEAPON_DOLL, METEOR_SIZE, WEAPON_BATTERY, BATTERY_HALF, WEAPON_GEYSER, WEAPONS, WEAPON_ROOMBA, ROOMBA_H, ROOMBA_W, ROOMBA_SIGHT, ROOMBA_MOUTH, WEAPON_ACID_BARREL, WEAPON_POWDER_BARREL, BARREL_SIZE, PROJ_ACID_DROP, FP_SHIFT, GEYSER_DELAY,
  type GameState, type JumpPad, type Unit,
} from '@invaders/game';
import { MATERIALS, Physique } from '@invaders/sim';
import type { Camera } from './camera';
import { TEAM_COLORS } from './teams';
import { drawHealthBar, type UnitAnimator } from './sprites';
import { drawChestArt } from './chest-art';

/** Screen position of a cell (top-left corner), in canvas pixels. */
export function toScreen(cam: Camera, x: number, y: number): [number, number] {
  return [(x - cam.x) * cam.scale, (y - cam.y) * cam.scale];
}

/**
 * A unit (GDD section 7): its animation, the bar of its health in the team colour over its head (HUD mockups),
 * and the ice or the flames on it. `bar` false: no bar (the placement ghost).
 */
export function drawUnit(ctx: CanvasRenderingContext2D, cam: Camera, unit: Unit, anim: UnitAnimator, now: number, alpha = 1, bar = true): void {
  if (!unit.alive) return;
  const s = cam.scale;
  const [sx, sy] = toScreen(cam, unit.x / FP, unit.y / FP);
  ctx.globalAlpha = alpha;
  if (unit.jet > 0) {
    // The jetpack on its back (behind the sprite), and its flame while it thrusts.
    const bx = sx + (unit.facing > 0 ? 0 : UNIT_W - 4) * s;
    ctx.fillStyle = '#2a2c33';
    ctx.fillRect(bx - s, sy + 4 * s, 5 * s, 9 * s);
    ctx.fillStyle = '#9aa0aa';
    ctx.fillRect(bx, sy + 5 * s, 3 * s, 7 * s);
    if (unit.thrust) {
      const flick = 2 + Math.floor((now / 60) % 3);
      ctx.fillStyle = '#ffb02e';
      ctx.fillRect(bx, sy + 13 * s, 3 * s, flick * s);
      ctx.fillStyle = '#fff4c0';
      ctx.fillRect(bx + s, sy + 13 * s, s, (flick - 1) * s);
    }
  }
  anim.draw(ctx, cam, unit, now, alpha);
  if (unit.drill > 0) drawDrillBit(ctx, s, sx + (UNIT_W / 2) * s, sy + (UNIT_H / 2) * s, unit.drillAngle, now);
  if (bar) drawHealthBar(ctx, cam, unit, TEAM_COLORS[unit.player % TEAM_COLORS.length], UNIT_HP);
  ctx.globalAlpha = alpha;
  // Frozen: a coat of ice over the body.
  if (unit.frozen !== 0) {
    ctx.fillStyle = 'rgba(190, 235, 255, 0.55)';
    ctx.fillRect(sx - s, sy - s, (UNIT_W + 2) * s, (UNIT_H + 2) * s);
    ctx.strokeStyle = 'rgba(235, 250, 255, 0.9)';
    ctx.lineWidth = Math.max(1, s / 2);
    ctx.strokeRect(sx - s, sy - s, (UNIT_W + 2) * s, (UNIT_H + 2) * s);
  }
  // On fire (a burn at the next resolution): flames on the head. Barbe Noire's lit beard: sparks.
  if (unit.burning || unit.fuse > 0) {
    const t = performance.now() / 80;
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = i % 2 === 0 ? '#ffb02e' : '#ff5a1f';
      const fx = sx + (3 + i * 3) * s;
      const fh = (2 + ((Math.sin(t + i * 1.7) + 1) * 1.5)) * s;
      if (unit.fuse > 0) ctx.fillRect(fx, sy + (UNIT_H - 4) * s - fh, s, fh); // beard
      else ctx.fillRect(fx, sy - fh, 2 * s, fh);
    }
  }
  ctx.globalAlpha = 1;
}

/** The drill's bit, a cone of steel turning ahead of a unit boring its way (from its centre, `angle` in degrees). */
function drawDrillBit(ctx: CanvasRenderingContext2D, s: number, cx: number, cy: number, angle: number, now: number): void {
  const a = (angle * Math.PI) / 180;
  const ux = Math.cos(a);
  const uy = -Math.sin(a);
  const at = (along: number, side: number): [number, number] => [cx + (ux * along - uy * side) * s, cy + (uy * along + ux * side) * s];
  const cone = (from: number, to: number, half: number) => {
    const [b1x, b1y] = at(from, -half);
    const [b2x, b2y] = at(from, half);
    const [tx, ty] = at(to, 0);
    ctx.beginPath(); ctx.moveTo(b1x, b1y); ctx.lineTo(b2x, b2y); ctx.lineTo(tx, ty); ctx.closePath(); ctx.fill();
  };
  ctx.fillStyle = '#5a5f69';
  cone(5, 17, 5);
  // Turning threads: light bands sliding toward the tip.
  const shift = (now / 40) % 3;
  for (let k = 0; k < 4; k++) {
    const d = 6 + k * 3 + shift;
    if (d > 16) continue;
    const half = 5 * (17 - d) / 12;
    const [p1x, p1y] = at(d, -half);
    const [p2x, p2y] = at(d + 1, half);
    ctx.beginPath(); ctx.moveTo(p1x, p1y); ctx.lineTo(p2x, p2y); ctx.lineWidth = Math.max(1, s / 2); ctx.strokeStyle = '#c8ccd2'; ctx.stroke();
  }
  ctx.fillStyle = '#ffd23f';
  cone(3, 6, 6);
}

/**
 * A unit in lava (standing in a shallow pool, or Lavaman and Cloudman, who do not drown in it) sits in it: the lava
 * inside the bottom of its box is drawn over its feet, its surface brighter.
 */
export function drawImmersion(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState): void {
  const { w, h, data } = state.world.physique;
  const s = cam.scale;
  const LAVA = Physique.LAVA;
  for (const u of state.units) {
    if (!u.alive) continue;
    const cx = Math.floor(u.x / FP);
    const cy = Math.floor(u.y / FP);
    for (let y = Math.max(1, cy + UNIT_H - 8); y < Math.min(h, cy + UNIT_H); y++) {
      for (let x = Math.max(0, cx); x < Math.min(w, cx + UNIT_W); x++) {
        if (data[y * w + x] !== LAVA) continue;
        ctx.fillStyle = data[(y - 1) * w + x] === LAVA ? '#e0501c' : '#ffa040';
        const [px, py] = toScreen(cam, x, y);
        ctx.fillRect(px, py, s, s);
      }
    }
  }
}

export function drawProjectiles(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState): void {
  const s = cam.scale;
  for (const p of state.projectiles) {
    const [sx, sy] = toScreen(cam, p.x / PFP, p.y / PFP);
    if (p.weapon === WEAPON_METEOR) {
      // The block of ice, with a trail.
      ctx.fillStyle = 'rgba(168, 228, 244, 0.35)';
      ctx.fillRect(sx + 3 * s, sy - 20 * s, (METEOR_SIZE - 6) * s, 20 * s);
      ctx.fillStyle = '#a8e4f4';
      ctx.fillRect(sx, sy, METEOR_SIZE * s, METEOR_SIZE * s);
      ctx.fillStyle = '#e8f8ff';
      ctx.fillRect(sx + s, sy + s, 4 * s, 2 * s);
      continue;
    }
    const size = Math.max(3, 3 * s);
    if (p.weapon === WEAPON_PISTOL) {
      // The bullet: a short bright streak behind it.
      const [tx, ty] = toScreen(cam, (p.x - p.vx * 0.6) / PFP, (p.y - p.vy * 0.6) / PFP);
      ctx.strokeStyle = 'rgba(255, 230, 140, 0.9)';
      ctx.lineWidth = Math.max(1, s * 0.8);
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(sx, sy); ctx.stroke();
      ctx.fillStyle = '#fff8e0';
      ctx.fillRect(sx - s / 2, sy - s / 2, s, s);
      continue;
    }
    if (p.weapon === WEAPON_LASER) {
      // The ray: from its start through its bounces on ice to its head.
      const m = p.marks ?? [];
      for (const [width, color] of [[4, 'rgba(255, 60, 60, 0.4)'], [1.5, 'rgba(255, 225, 225, 0.95)']] as const) {
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(1, width * s * 0.5);
        ctx.beginPath();
        for (let i = 0; i < m.length; i += 2) {
          const [x, y] = toScreen(cam, m[i] + 0.5, m[i + 1] + 0.5);
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.lineTo(sx, sy);
        ctx.stroke();
      }
      continue;
    }
    if (p.weapon === PROJ_LAVA_BOMB) {
      // A glowing lava bomb, with a trail of embers behind it.
      for (let k = 1; k <= 3; k++) {
        const [tx, ty] = toScreen(cam, (p.x - p.vx * k * 2) / PFP, (p.y - p.vy * k * 2) / PFP);
        ctx.fillStyle = `rgba(255, ${150 - k * 30}, 40, ${0.5 - k * 0.12})`;
        ctx.fillRect(tx - size / 2, ty - size / 2, size, size);
      }
      ctx.fillStyle = '#ff5a1f';
      ctx.fillRect(sx - size * 0.75, sy - size * 0.75, size * 1.5, size * 1.5);
      ctx.fillStyle = '#ffd23f';
      ctx.fillRect(sx - size / 4, sy - size / 4, size / 2, size / 2);
      continue;
    }
    if (p.weapon === PROJ_ACID_DROP) {
      ctx.fillStyle = '#8fe03a';
      ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
      continue;
    }
    if (p.weapon === WEAPON_ACID_BARREL || p.weapon === WEAPON_POWDER_BARREL) {
      // In the air: the barrel itself, turning.
      drawBarrelArt(ctx, sx, sy, s, p.weapon, p.age * 0.2);
      continue;
    }
    if (p.weapon === WEAPON_ROOMBA) {
      drawRoombaArt(ctx, sx - (ROOMBA_W * s) / 2, sy - (ROOMBA_H * s) / 2, s, p.vx < 0 ? -1 : 1, false);
      continue;
    }
    if (p.weapon === WEAPON_DOLL) {
      // A little cloth doll, and the thread to the enemy it is bound to.
      ctx.fillStyle = '#c69cff';
      ctx.fillRect(sx - 1.5 * s, sy - 3 * s, 3 * s, 6 * s);
      ctx.fillRect(sx - 3 * s, sy - 1 * s, 6 * s, 2 * s);
      const t = p.link >= 0 ? state.units[p.link] : undefined;
      if (t?.alive) {
        const [tx, ty] = toScreen(cam, t.x / FP + UNIT_W / 2, t.y / FP + UNIT_H / 2);
        ctx.strokeStyle = 'rgba(198, 156, 255, 0.6)';
        ctx.setLineDash([s, 2 * s]);
        ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(tx, ty); ctx.stroke();
        ctx.setLineDash([]);
      }
      continue;
    }
    ctx.fillStyle = p.weapon === WEAPON_GRENADE ? '#3b5d2a' : p.weapon === WEAPON_SANDBAG ? '#d6b85a' : p.weapon === WEAPON_MINE ? '#c03030'
      : p.weapon === WEAPON_CRYO ? '#7fd0f0' : p.weapon === WEAPON_ICE_MINE ? '#3f8fd0' : p.weapon === WEAPON_COCKTAIL ? '#9a7a2a'
      : p.weapon === WEAPON_BATTERY ? '#ffd23f' : '#e8e8e8';
    ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
    if (p.weapon === WEAPON_GRENADE || p.weapon === WEAPON_CRYO) {
      const left = Math.ceil((GRENADE_FUSE_TICKS - p.age) / TICKS_PER_SECOND);
      ctx.fillStyle = '#fff';
      ctx.font = `${Math.max(10, 3 * s)}px ui-monospace, monospace`;
      ctx.textAlign = 'center';
      ctx.fillText(String(left), sx, sy - size);
    }
  }
}

/** Steles (GDD section 6): a small tombstone in the team colour, with the legacy turns left. */
export function drawSteles(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState): void {
  const s = cam.scale;
  ctx.textAlign = 'center';
  ctx.font = `bold ${Math.max(9, 2.5 * s)}px system-ui, sans-serif`;
  for (const st of state.steles) {
    const [sx, sy] = toScreen(cam, st.x - 3, st.y - 7);
    ctx.fillStyle = '#9aa0a8';
    ctx.fillRect(sx, sy + s, 7 * s, 7 * s);
    ctx.fillRect(sx + s, sy, 5 * s, s);
    ctx.fillStyle = TEAM_COLORS[st.player % TEAM_COLORS.length];
    ctx.fillRect(sx + s, sy + 2 * s, 5 * s, s);
    ctx.fillStyle = '#fff';
    ctx.fillText(String(LEGACY_TURNS - st.stage), sx + 3.5 * s, sy - s);
  }
}

/** Chests (GDD section 9): the GDD's pixel art (chest-art.ts), a parachute while it comes down. */
export function drawChests(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState): void {
  const s = cam.scale;
  const now = performance.now();
  for (const c of state.chests) {
    const [sx, sy] = toScreen(cam, c.x / FP, c.y / FP);
    if (c.mode === CHEST_FALLING || c.mode === CHEST_SHORE) {
      // Canopy and strings, tied to the top corners of the art.
      const cx = sx + (CHEST_W * s) / 2;
      const top = sy - s;
      const canopy = top - 10 * s;
      ctx.fillStyle = '#e8e2d0';
      ctx.beginPath();
      ctx.arc(cx, canopy, 9 * s, Math.PI, 0);
      ctx.fill();
      ctx.fillStyle = '#c0392b';
      ctx.beginPath();
      ctx.arc(cx, canopy, 9 * s, Math.PI, Math.PI * 1.33);
      ctx.lineTo(cx, canopy);
      ctx.fill();
      ctx.strokeStyle = 'rgba(230,230,230,0.8)';
      ctx.lineWidth = Math.max(1, s / 3);
      ctx.beginPath();
      ctx.moveTo(cx - 9 * s, canopy); ctx.lineTo(sx - s, top);
      ctx.moveTo(cx + 9 * s, canopy); ctx.lineTo(sx + (CHEST_W + 1) * s, top);
      ctx.stroke();
    }
    drawChestArt(ctx, cam, c, sx, sy, now);
  }
}

/** Mines: a small red disc; its light blinks once armed. */
export function drawMines(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState, now: number): void {
  const s = cam.scale;
  for (const m of state.mines) {
    const [sx, sy] = toScreen(cam, m.x - MINE_HALF, m.y - MINE_HALF);
    ctx.fillStyle = '#3a3d46';
    ctx.fillRect(sx, sy + s, (2 * MINE_HALF + 1) * s, 2 * MINE_HALF * s);
    const lit = isArmed(state, m) ? Math.floor(now / 300) % 2 === 0 : false;
    const ice = m.kind === MINE_ICE;
    ctx.fillStyle = lit ? (ice ? '#9fe8ff' : '#ff3030') : ice ? '#2f5f80' : '#7a2020';
    ctx.fillRect(sx + MINE_HALF * s, sy, s, s);
  }
}

const ROOMBA_COLORS: Readonly<Record<string, string>> = { k: '#15151c', G: '#8a8f99', g: '#c8ccd4', m: '#5a1420', t: '#ff7a8a' };
/** A round vacuum cleaner with the face of an emoji, :) , on the side it walks to; its mouth opens wide to swallow. */
const ROOMBA_BODY = ['...kkkkkkkk...', '.kkggggggggkk.', 'kgggggkggkgggk', 'kgggggkggkgggk', 'kgggkggggggkgk', 'kGgggkkkkkkggk', '.kGggggggggGk.', '..kkkkkkkkkk..'];
const ROOMBA_OPEN = ['...kkkkkkkk...', '.kkggggggggkk.', 'kgggggkggkgggk', 'kgggggkggkgggk', 'kggggggkmmmkgk', 'kGgggggkmttkgk', '.kGggggkkkkgk.', '..kkkkkkkkkk..'];

/** The Roomba, the top-left of its box at (x, y). */
function drawRoombaArt(ctx: CanvasRenderingContext2D, px: number, py: number, s: number, dir: number, open: boolean): void {
  // On whole pixels: at a fractional position, thin lines would show between the cells of the drawing.
  const x = Math.round(px);
  const y = Math.round(py);
  (open ? ROOMBA_OPEN : ROOMBA_BODY).forEach((row, ry) => {
    for (let rx = 0; rx < row.length; rx++) {
      const c = ROOMBA_COLORS[row[rx]];
      if (!c) continue;
      ctx.fillStyle = c;
      ctx.fillRect(x + (dir > 0 ? rx : row.length - 1 - rx) * s, y + ry * s, s, s);
    }
  });
}

/** The whirlwind in front of a mouth at (mx, my), like Kirby's: rings that close in on it, and what it swallows, flying in. */
function drawWhirlwind(ctx: CanvasRenderingContext2D, mx: number, my: number, s: number, dir: number, now: number, color: string): void {
  const reach = ROOMBA_SIGHT * s;
  ctx.lineWidth = Math.max(1, s / 2);
  for (let k = 0; k < 6; k++) {
    const d = 1 - (((now / 500 + k / 6) % 1));
    const x = mx + dir * d * reach;
    const ry = (1.5 + d * 11) * s;
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.15 + 0.5 * (1 - d)})`;
    ctx.beginPath();
    ctx.ellipse(x, my + Math.sin(now / 120 + k) * s, ry * 0.3, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = color;
  for (let k = 0; k < 14; k++) {
    const d = 1 - (((now / 420 + k * 0.137) % 1));
    const turn = now / 90 + k * 2.4;
    ctx.fillRect(mx + dir * d * reach - s / 2, my + Math.sin(turn) * d * 11 * s - s / 2, s, s);
  }
}

/** A barrel, its centre at (sx, sy): staves of wood, iron hoops, a mark of what it holds that turns with it. */
function drawBarrelArt(ctx: CanvasRenderingContext2D, sx: number, sy: number, s: number, weapon: number, turn: number): void {
  const size = BARREL_SIZE * s;
  const x = sx - size / 2;
  const y = sy - size / 2;
  ctx.fillStyle = '#6e4322';
  ctx.fillRect(x + s, y, size - 2 * s, size);
  ctx.fillRect(x, y + s, size, size - 2 * s);
  ctx.fillStyle = '#8a5a2e';
  ctx.fillRect(x + 2 * s, y + s, size - 4 * s, size - 2 * s);
  ctx.fillStyle = '#4a4f58';
  ctx.fillRect(x, y + 2 * s, size, s);
  ctx.fillRect(x, y + size - 3 * s, size, s);
  const a = turn;
  ctx.fillStyle = weapon === WEAPON_ACID_BARREL ? '#8fe03a' : '#26262b';
  ctx.fillRect(sx + Math.cos(a) * 2 * s - s, sy + Math.sin(a) * 2 * s - s, 2 * s, 2 * s);
  ctx.fillRect(sx - Math.cos(a) * 2 * s - s / 2, sy - Math.sin(a) * 2 * s - s / 2, s, s);
}

/**
 * What the machines of the arsenal and the maps add over the terrain: the barrels (they turn as they roll, a dot per
 * turn left), the mark of a geyser waiting to spring, the chevrons of the conveyor belts, the jump pads.
 */
export function drawMachines(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState, now: number): void {
  const s = cam.scale;
  for (const belt of state.map.belts) {
    const step = 12;
    const shift = ((now / 40) * belt.dir) % step;
    for (let c = belt.x0; c <= belt.x1 - 4; c += step) {
      const x = c + (shift + step) % step;
      if (x > belt.x1 - 4) continue;
      const [sx, sy] = toScreen(cam, x, belt.y);
      ctx.fillStyle = '#ffd23f';
      ctx.fillRect(sx, sy, 4 * s, s);
      ctx.fillStyle = '#26262b';
      ctx.fillRect(sx + 4 * s, sy, 8 * s, s);
    }
  }
  for (const p of state.map.pads) drawPad(ctx, cam, state, p, now);
  for (const b of state.barrels) {
    const cx = b.x / FP;
    const by = b.y >> FP_SHIFT;
    const [sx, sy] = toScreen(cam, cx, by + 1 - BARREL_SIZE / 2);
    drawBarrelArt(ctx, sx, sy, s, b.weapon, cx / 3);
    ctx.fillStyle = '#fff';
    for (let k = 0; k < b.turns; k++) ctx.fillRect(sx + (3 * k - 3) * s, sy - (BARREL_SIZE / 2 + 2) * s, s, s);
  }
  for (const r of state.roombas) {
    const cx = r.x / FP;
    const by = r.y >> FP_SHIFT;
    const [sx, sy] = toScreen(cam, cx - ROOMBA_W / 2, by + 1 - ROOMBA_H);
    drawRoombaArt(ctx, sx, sy, s, r.dir, r.sucking);
    if (r.sucking) {
      const [mx, my] = toScreen(cam, cx + (r.dir * ROOMBA_W) / 2, by + 0.5 - ROOMBA_MOUTH);
      const c = MATERIALS[r.ate]?.color ?? 0xffffff;
      drawWhirlwind(ctx, mx, my, s, r.dir, now, `#${c.toString(16).padStart(6, '0')}`);
    }
    ctx.fillStyle = '#fff';
    for (let k = 0; k < r.turns; k++) ctx.fillRect(sx + (ROOMBA_W / 2 + 3 * k - 3) * s, sy - 3 * s, s, s);
  }
  for (const g of state.geysers) {
    if (g.wait === 0) continue;
    // Bubbling ground, and the seconds left once the next turn has begun.
    const [sx, sy] = toScreen(cam, g.x, g.y);
    const half = (WEAPONS[WEAPON_GEYSER].radius + 0.5) * s;
    ctx.fillStyle = 'rgba(95, 180, 216, 0.55)';
    ctx.fillRect(sx - half, sy - s, 2 * half, s);
    ctx.fillStyle = '#c8ecf8';
    for (let k = -2; k <= 2; k++) {
      const up = (now / 90 + k * 37) % 7;
      ctx.fillRect(sx + (k * half) / 2.5, sy - (2 + up) * s, s, s);
    }
    ctx.textAlign = 'center';
    ctx.font = `bold ${Math.max(10, 3 * s)}px ui-monospace, monospace`;
    ctx.fillStyle = '#fff';
    const counting = g.wait < GEYSER_DELAY || state.match === null || state.match.turn !== g.turn;
    ctx.fillText(counting ? String(Math.ceil(g.wait / TICKS_PER_SECOND)) : '…', sx, sy - 10 * s);
  }
}

/** Batteries (author's addition): a grey case with a yellow bolt, sparks now and then; a dot per turn left. */
export function drawBatteries(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState, now: number): void {
  const s = cam.scale;
  for (const b of state.batteries) {
    const [sx, sy] = toScreen(cam, b.x - BATTERY_HALF, b.y - BATTERY_HALF);
    const size = 2 * BATTERY_HALF + 1;
    ctx.fillStyle = '#15151c';
    ctx.fillRect(sx - s, sy - s, (size + 2) * s, (size + 1) * s);
    ctx.fillStyle = '#9aa0aa';
    ctx.fillRect(sx, sy, size * s, size * s);
    ctx.fillStyle = '#5a5f69';
    ctx.fillRect(sx + BATTERY_HALF * s, sy - 2 * s, s, s);
    ctx.fillStyle = '#ffd23f';
    ctx.fillRect(sx + BATTERY_HALF * s, sy + s, s, (size - 2) * s);
    for (let k = 0; k < b.turns; k++) ctx.fillRect(sx + k * 2 * s, sy - 4 * s, s, s);
    if (Math.floor(now / 140 + b.x) % 7 === 0) {
      // A spark.
      ctx.fillStyle = '#fff4a0';
      ctx.fillRect(sx + (size + 1) * s, sy - 2 * s, s, s);
      ctx.fillRect(sx - 2 * s, sy + s, s, s);
    }
  }
}

/** La Vague: a curling wall of water drawn over its front (the trail is real water cells). */
export function drawSurges(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState, now: number): void {
  const s = cam.scale;
  for (const sv of state.surges) {
    const body = 8;
    const x0 = sv.dir > 0 ? sv.x - body : sv.x + 1;
    const [sx, sy] = toScreen(cam, x0, sv.floor - sv.height);
    const width = (body + 1) * s;
    const height = sv.height * s;
    const g = ctx.createLinearGradient(0, sy, 0, sy + height);
    g.addColorStop(0, 'rgba(210, 235, 255, 0.95)');
    g.addColorStop(0.25, 'rgba(70, 140, 225, 0.9)');
    g.addColorStop(1, 'rgba(40, 100, 200, 0.85)');
    ctx.fillStyle = g;
    ctx.beginPath();
    const front = sv.dir > 0 ? sx + width : sx;
    const back = sv.dir > 0 ? sx : sx + width;
    ctx.moveTo(back, sy + height);
    ctx.lineTo(back, sy + height * 0.35);
    ctx.quadraticCurveTo(back + (front - back) * 0.4, sy - 2 * s, front + sv.dir * 3 * s, sy + 3 * s);
    ctx.lineTo(front, sy + height);
    ctx.closePath();
    ctx.fill();
    // Foam on the crest.
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    for (let i = 0; i < 4; i++) {
      const fx = back + (front - back) * (0.5 + 0.15 * i) + Math.sin(now / 90 + i) * s;
      ctx.fillRect(fx, sy + (i % 2) * s, s, s);
    }
  }
}

/**
 * A jump pad: a plate striped yellow and black over springs, in the top rows of the ground it stands on (the units walk
 * on its row). Not drawn once the ground under its middle is gone: it no longer throws anyone.
 */
export function drawPad(ctx: CanvasRenderingContext2D, cam: Camera, state: GameState, pad: JumpPad, now: number): void {
  const { w, h, data } = state.world.physique;
  const mid = (pad.x0 + pad.x1) >> 1;
  if (pad.y < 0 || pad.y >= h || data[pad.y * w + mid] === 0) return;
  const s = cam.scale;
  const [sx, sy] = toScreen(cam, pad.x0, pad.y);
  const width = pad.x1 - pad.x0 + 1;
  // A little bounce of the plate, as if it were alive.
  const lift = Math.sin(now / 160) > 0.92 ? s : 0;
  ctx.fillStyle = '#2b2d33';
  ctx.fillRect(sx, sy + 2 * s, width * s, 3 * s);
  ctx.fillStyle = '#b8c4cc';
  for (let c = 2; c < width - 1; c += 4) ctx.fillRect(sx + c * s, sy + 2 * s - lift, s, 2 * s + lift);
  for (let c = 0; c < width; c++) {
    ctx.fillStyle = ((c >> 1) & 1) === 0 ? '#ffd23f' : '#26262b';
    ctx.fillRect(sx + c * s, sy - lift, s, 2 * s);
  }
  ctx.fillStyle = '#ff4a3d';
  ctx.fillRect(sx + (width / 2 - 1) * s, sy - 2 * s - lift, 2 * s, s);
  ctx.fillRect(sx + (width / 2 - 2) * s, sy - s - lift, 4 * s, s);
}
